
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';

import {
    Alert,
    Box,
    Button,
    Checkbox,
    Container,
    FormControlLabel,
    InputAdornment,
    Paper,
    TextField,
    Typography
} from '@mui/material';

import {
    SecurityOutlined,
    SmartphoneOutlined,
    LockOutlined,
    HelpOutlineOutlined,
    ArrowForwardRounded
} from '@mui/icons-material';

import { LoadingButton } from '@mui/lab';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { verifyCodeAuth2fa } from '../../services/User';
import { saveAuth } from '../../hooks/useAuth';
import { getPublicIp } from '../../utils';

function Login2FA() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [serverError, setServerError] = useState('');
    const [showHelp, setShowHelp] = useState(false);

    const routeState = location.state || {};

    const active2fa = Number(routeState.active2fa) === 1;
    const id2fa = routeState.id2fa;

    const isValidSession =
        active2fa &&
        id2fa !== null &&
        id2fa !== undefined &&
        String(id2fa).trim() !== '';

    const initialValues = {
        codeauth: '',
        remember: false
    };

    const validationSchema = Yup.object().shape({
        codeauth: Yup.string()
            .required(t('codeRequired'))
            .matches(/^[0-9]{6}$/, t('codeSixDigits'))
    });

    const confirmCode = async (values, actions) => {
        setServerError('');

        try {
            const ip = await getPublicIp();

            const result = await verifyCodeAuth2fa(
                id2fa,
                values.codeauth,
                ip,
                values.remember
            );

            if (result && result.error === false && result.verify === true) {

                if (Number(result.accounts) === 1) {
                    saveAuth(result);

                    if (routeState.redirectTo) {
                        navigate(routeState.redirectTo, {
                            replace: true
                        });
                    } else {
                        navigate('/clientarea/dashboard/', {
                            replace: true
                        });
                    }

                    return;
                }

                navigate('/login/accounts/', {
                    replace: true,
                    state: {
                        accounts: result.accountsdata,
                        uid: result.uid,
                        language: result.language
                    }
                });

                return;
            }

            // Código rechazado por el servidor.
            setServerError(t('invalidCode'));

            actions.setFieldValue('codeauth', '', false);
            actions.setFieldTouched('codeauth', false, false);

        } catch (error) {
            // Fallo técnico o de conexión.
            setServerError(t('verificationConnectionError'));

        } finally {
            actions.setSubmitting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>{t('clientAreaTitle')}</title>
            </Helmet>

            <Box
                sx={{
                    minHeight: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 3, md: 5 },
                    bgcolor: 'transparent'
                }}
            >
                <Container
                    maxWidth="sm"
                    disableGutters
                    sx={{
                        maxWidth: '480px !important'
                    }}
                >
                    {isValidSession ? (

                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: { xs: 2.5, sm: 3 },
                                border: '1px solid',
                                borderColor: '#e7ecf3',
                                bgcolor: '#ffffff',
                                boxShadow:
                                    '0 16px 48px rgba(20, 35, 65, 0.055)',
                                px: { xs: 2.5, sm: 4.5 },
                                py: { xs: 3, sm: 4.5 }
                            }}
                        >
                            {/* Encabezado */}

                            <Box
                                sx={{
                                    textAlign: 'center',
                                    mb: 3.5
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 68,
                                        height: 68,
                                        borderRadius: '20px',
                                        bgcolor: '#eef2ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: 2.5
                                    }}
                                >
                                    <SecurityOutlined
                                        sx={{
                                            fontSize: 38,
                                            color: '#5f81ff'
                                        }}
                                    />
                                </Box>

                                <Typography
                                    component="h1"
                                    sx={{
                                        fontSize: {
                                            xs: '1.6rem',
                                            sm: '1.9rem'
                                        },
                                        fontWeight: 700,
                                        color: '#182337',
                                        letterSpacing: '-0.025em',
                                        lineHeight: 1.3,
                                        mb: 1.5
                                    }}
                                >
                                    {t('verifyYourIdentity')}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: '1.4rem',
                                        color: '#64748b',
                                        lineHeight: 1.75,
                                        maxWidth: 355,
                                        mx: 'auto'
                                    }}
                                >
                                    {t('twoFactorDescription')}
                                </Typography>
                            </Box>

                            {/* Instrucciones */}

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    bgcolor: '#f5f7ff',
                                    border: '1px solid #e7ecff',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 2,
                                    mb: 3.5
                                }}
                            >
                                <SmartphoneOutlined
                                    sx={{
                                        color: '#5f81ff',
                                        fontSize: 24,
                                        mt: 0.2,
                                        flexShrink: 0
                                    }}
                                />

                                <Box>
                                    <Typography
                                        sx={{
                                            color: '#26344d',
                                            fontSize: '1.4rem',
                                            fontWeight: 700,
                                            mb: 0.7
                                        }}
                                    >
                                        {t('whereFindCode')}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: '#5c6b83',
                                            fontSize: '1.25rem',
                                            lineHeight: 1.7
                                        }}
                                    >
                                        {t('authenticatorInstructions')}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Formulario */}

                            <Formik
                                initialValues={initialValues}
                                validationSchema={validationSchema}
                                onSubmit={confirmCode}
                            >
                                {({
                                    values,
                                    errors,
                                    touched,
                                    handleBlur,
                                    setFieldValue,
                                    isSubmitting
                                }) => {

                                    const isCodeComplete =
                                        values.codeauth.length === 6;

                                    const handleCodeChange = (event) => {
                                        const value = event.target.value
                                            .replace(/\D/g, '')
                                            .slice(0, 6);

                                        setFieldValue('codeauth', value);

                                        if (serverError) {
                                            setServerError('');
                                        }
                                    };

                                    const codeHasError =
                                        Boolean(
                                            touched.codeauth &&
                                            errors.codeauth
                                        );

                                    return (
                                        <Form noValidate>

                                            {serverError && (
                                                <Alert
                                                    severity="error"
                                                    role="alert"
                                                    sx={{
                                                        mb: 2.5,
                                                        borderRadius: 2,
                                                        fontSize: '1.15rem',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    {serverError}
                                                </Alert>
                                            )}

                                            <Typography
                                                component="label"
                                                htmlFor="codeauth"
                                                sx={{
                                                    display: 'block',
                                                    fontSize: '1.45rem',
                                                    fontWeight: 700,
                                                    color: '#26344d',
                                                    mb: 1.2
                                                }}
                                            >
                                                {t('verificationCodeLabel')}
                                            </Typography>

                                            <TextField
                                                id="codeauth"
                                                name="codeauth"
                                                value={values.codeauth}
                                                onChange={handleCodeChange}
                                                onBlur={handleBlur}
                                                fullWidth
                                                autoFocus
                                                variant="outlined"
                                                type="text"
                                                placeholder="000000"
                                                disabled={isSubmitting}
                                                error={codeHasError}
                                                autoComplete="one-time-code"
                                                inputProps={{
                                                    inputMode: 'numeric',
                                                    pattern: '[0-9]*',
                                                    maxLength: 6,
                                                    'aria-label': t(
                                                        'verificationCodeLabel'
                                                    )
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        height: 66,
                                                        borderRadius: 2,
                                                        bgcolor: '#fff',
                                                        '& fieldset': {
                                                            borderColor: '#dce3ed'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: '#9aabca'
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#5f81ff',
                                                            borderWidth: 2
                                                        }
                                                    },
                                                    '& input': {
                                                        textAlign: 'center',
                                                        fontSize: {
                                                            xs: '2rem',
                                                            sm: '3rem'
                                                        },
                                                        fontWeight: 700,
                                                        letterSpacing: '0.35em',
                                                        fontVariantNumeric:
                                                            'tabular-nums',
                                                        color: '#182337',
                                                        paddingLeft:
                                                            'calc(14px + 0.35em)',
                                                        '&::placeholder': {
                                                            color: '#bcc6d5',
                                                            opacity: 1
                                                        }
                                                    }
                                                }}
                                            />

                                            <Typography
                                                sx={{
                                                    fontSize: '1.1rem',
                                                    color: codeHasError
                                                        ? '#d32f2f'
                                                        : '#8491a4',
                                                    mt: 1,
                                                    minHeight: 19,
                                                    lineHeight: 1.5
                                                }}
                                            >
                                                {codeHasError
                                                    ? errors.codeauth
                                                    : t('codeRefreshHint')}
                                            </Typography>

                                            {/* Recordar dispositivo */}

                                            <FormControlLabel
                                                sx={{
                                                    mt: 2.5,
                                                    mb: 2.5,
                                                    ml: -1,
                                                    alignItems: 'flex-start',
                                                    '& .MuiFormControlLabel-label': {
                                                        pt: '9px'
                                                    }
                                                }}
                                                control={
                                                    <Checkbox
                                                        name="remember"
                                                        checked={values.remember}
                                                        disabled={isSubmitting}
                                                        onChange={(event) => {
                                                            setFieldValue(
                                                                'remember',
                                                                event.target.checked
                                                            );
                                                        }}
                                                        sx={{
                                                            color: '#aab5c6',
                                                            '&.Mui-checked': {
                                                                color: '#5f81ff'
                                                            }
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Typography
                                                        sx={{
                                                            fontSize: '1.2rem',
                                                            color: '#4e5d72',
                                                            lineHeight: 1.55
                                                        }}
                                                    >
                                                        {t('trustedDevice')}
                                                    </Typography>
                                                }
                                            />

                                            {/* Botón principal */}

                                            <LoadingButton
                                                type="submit"
                                                loading={isSubmitting}
                                                disabled={
                                                    !isCodeComplete ||
                                                    isSubmitting
                                                }
                                                variant="contained"
                                                disableElevation
                                                fullWidth
                                                endIcon={
                                                    !isSubmitting
                                                        ? <ArrowForwardRounded />
                                                        : null
                                                }
                                                sx={{
                                                    height: 53,
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    fontSize: '1.3rem',
                                                    bgcolor: '#5f81ff',
                                                    boxShadow: 'none',
                                                    '&:hover': {
                                                        bgcolor: '#496beb',
                                                        boxShadow: 'none'
                                                    },
                                                    '&.Mui-disabled': {
                                                        bgcolor: '#e9edf4',
                                                        color: '#93a0b2'
                                                    }
                                                }}
                                            >
                                                {isSubmitting
                                                    ? t('verifyingCode')
                                                    : t('verifyAndContinue')}
                                            </LoadingButton>

                                        </Form>
                                    );
                                }}
                            </Formik>

                            {/* Ayuda */}

                            <Box sx={{ mt: 2.5, textAlign: 'center' }}>
                                <Button
                                    type="button"
                                    size="small"
                                    startIcon={<HelpOutlineOutlined />}
                                    onClick={() => setShowHelp(!showHelp)}
                                    aria-expanded={showHelp}
                                    sx={{
                                        fontSize: '1rem',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        color: '#5f75cc',
                                        '&:hover': {
                                            bgcolor: '#f4f6ff'
                                        }
                                    }}
                                >
                                    {t('cantFindCode')}
                                </Button>
                            </Box>

                            {showHelp && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        px: 2,
                                        py: 2,
                                        borderRadius: 2,
                                        bgcolor: '#f8fafc',
                                        border: '1px solid #e9edf3'
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '1.1rem',
                                            color: '#26344d',
                                            mb: 1
                                        }}
                                    >
                                        {t('authenticatorHelpTitle')}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: '1rem',
                                            color: '#64748b',
                                            lineHeight: 1.7
                                        }}
                                    >
                                        {t('authenticatorHelpDescription')}
                                    </Typography>
                                </Box>
                            )}

                        </Paper>

                    ) : (

                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 3, sm: 5 },
                                borderRadius: 3,
                                border: '1px solid #e7ecf3',
                                textAlign: 'center'
                            }}
                        >
                            <SecurityOutlined
                                sx={{
                                    fontSize: 50,
                                    color: '#94a3b8',
                                    mb: 2
                                }}
                            />

                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    mb: 1.5
                                }}
                            >
                                {t('sorry')}
                            </Typography>

                            <Typography
                                color="textSecondary"
                                sx={{
                                    mb: 3,
                                    lineHeight: 1.7
                                }}
                            >
                                {t('optionUnavailable')}
                            </Typography>

                            <Button
                                variant="contained"
                                onClick={() => {
                                    navigate('/login/', {
                                        replace: true
                                    });
                                }}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2
                                }}
                            >
                                {t('backToLogin')}
                            </Button>
                        </Paper>
                    )}

                </Container>
            </Box>
        </>
    );
}

export default Login2FA;